import { useEffect } from "react"

import type { AnimationEvent, AnimationPlayer, PitchSnapshot } from "@/types"

import { PitchGrid } from "../replay/pitch-grid"

import { useMiniReplayReducer } from "./use-mini-replay-reducer"

interface MiniReplayCardProps {
  replaySnapshot: PitchSnapshot
  replayEvents: AnimationEvent[]
  playersData: Record<string, AnimationPlayer>
  homeTeamId: string
  homeTeamHue: number
  awayTeamHue: number
  paused: boolean
  onEventChange?: (eventIndex: number) => void
}

const COLS = 26
const ROWS = 15

export function MiniReplayCard({
  replaySnapshot,
  replayEvents,
  playersData,
  homeTeamId,
  homeTeamHue,
  awayTeamHue,
  paused,
  onEventChange,
}: MiniReplayCardProps) {
  const { state, derived } = useMiniReplayReducer(
    replaySnapshot,
    replayEvents,
    playersData,
    paused,
  )

  const { pitchState, highlightType, eventKey, progress } = state
  const displayedEventIndex = state.eventIndex > 0 ? state.eventIndex - 1 : -1
  const currentEvent =
    displayedEventIndex >= 0 ? replayEvents[displayedEventIndex] : null

  useEffect(() => {
    onEventChange?.(displayedEventIndex)
  }, [displayedEventIndex, onEventChange])
  const { ballPosition: ballPos } = derived
  const totalEvents = replayEvents.length

  return (
    <div className="relative h-full w-full">
      {/* Pitch grid — fills entire card (compact: no NeonFrame) */}
      <PitchGrid
        pitchState={pitchState}
        homeTeamId={homeTeamId}
        homeTeamHue={homeTeamHue}
        awayTeamHue={awayTeamHue}
        actingPlayerId={currentEvent?.actingPlayerId ?? null}
        highlightType={highlightType}
        eventKey={eventKey}
        compact
      />

      {/* Ball position indicator overlay */}
      {ballPos && (
        <div
          className="pointer-events-none absolute"
          style={{
            // PitchGrid has p-2 (0.5rem) inset. Card and pitch share 26/15
            // aspect ratio so the grid fills the container minus padding.
            left: `calc(0.5rem + (100% - 1rem) * ${(ballPos[0] + 0.5) / COLS})`,
            top: `calc(0.5rem + (100% - 1rem) * ${(ballPos[1] + 0.5) / ROWS})`,
            transform: "translate(-50%, -50%)",
            zIndex: 20,
            transition: "left 0.3s ease-out, top 0.3s ease-out",
          }}
        >
          {/* Outer glow ring */}
          <div
            className="absolute rounded-full"
            style={{
              width: 24,
              height: 24,
              top: -12,
              left: -12,
              background:
                "radial-gradient(circle, oklch(0.78 0.18 55 / 0.4) 0%, transparent 70%)",
            }}
          />
          {/* Ball dot */}
          <div
            className="rounded-full border-2"
            style={{
              width: 10,
              height: 10,
              marginTop: -5,
              marginLeft: -5,
              background:
                "radial-gradient(circle at 35% 35%, oklch(0.85 0.15 55), oklch(0.60 0.15 45))",
              borderColor: "oklch(0.50 0.12 45)",
              boxShadow:
                "0 0 8px oklch(0.78 0.18 55 / 0.6), 0 1px 3px oklch(0 0 0 / 0.5)",
            }}
          />
        </div>
      )}

      {/* Timeline bar — overlaid at the bottom */}
      {totalEvents > 0 && (
        <div
          className="pointer-events-none absolute right-3 bottom-2 left-3"
          style={{ zIndex: 20 }}
        >
          <div
            className="h-1 w-full overflow-hidden rounded-full"
            style={{ background: "oklch(0.15 0.02 55 / 0.7)" }}
          >
            <div
              className="h-full rounded-full"
              style={{
                width: `${progress * 100}%`,
                background:
                  "linear-gradient(90deg, oklch(0.65 0.15 55), oklch(0.78 0.18 55))",
                boxShadow: "0 0 6px oklch(0.78 0.18 55 / 0.5)",
                transition: "width 0.3s ease-out",
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
